import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useProcess } from '@/api/queries/useProcesses';
import { useProcessLayoutForView } from '@/lib/useProcessLayoutForView';
import { processErrorMessageKey } from '@/lib/process-utils';
import {
  Button,
  Skeleton,
  ProcessFlowVisualization,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@bliks-io/ui';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useAssigneeLookup } from '@/lib/useAssigneeLookup';
import { useProcessEntitySuggestions } from '@/lib/useProcessEntitySuggestions';
import {
  buildHeatmapOverlays,
  HEATMAP_VIEW_MODES,
  maxStepHoursPerYear,
  type HeatmapViewMode
} from '@/lib/processFlowHeatmap';
import { ProcessFlowHeatmapLegend } from './components/ProcessFlowHeatmapLegend';

interface ProcessFlowFullscreenPageProps {
  processId: string;
  viewMode: HeatmapViewMode;
  onViewModeChange: (mode: HeatmapViewMode) => void;
}

export function ProcessFlowFullscreenPage({
  processId,
  viewMode,
  onViewModeChange
}: ProcessFlowFullscreenPageProps) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useProcess(processId);
  const layout = useProcessLayoutForView(processId, true);
  const assigneeLookup = useAssigneeLookup();

  const process = data?.process;
  const entitySuggestions = useProcessEntitySuggestions(process, data?.entitySuggestions);

  useDocumentTitle('pageTitles.processFlowFullscreen', { name: process?.name || 'Loading...' });

  // Both heatmap dimensions read step surveys and `stepCapacities`, which the
  // server strips without `readSensitive` (ADR 0021) — offering the control to
  // an insights-only viewer would only ever draw an empty overlay.
  const canViewSensitiveFields = data?.capabilities?.canViewSensitiveFields ?? false;

  const overlays = useMemo(
    () =>
      viewMode === 'standard' || !canViewSensitiveFields
        ? undefined
        : buildHeatmapOverlays(
            viewMode,
            data?.process?.processStepRecordings ?? [],
            data?.capacity?.perStep
          ),
    [viewMode, data, canViewSensitiveFields]
  );
  const maxHoursPerYear = useMemo(
    () => maxStepHoursPerYear(data?.process?.processStepRecordings ?? [], data?.capacity?.perStep),
    [data]
  );

  if (isLoading) {
    return (
      <div className="h-screen flex flex-col bg-surface-subtle">
        <div className="border-b border-line bg-white px-6 py-4">
          <Skeleton className="h-6 w-64" />
        </div>
        <div className="flex-1 p-6">
          <Skeleton className="h-full w-full" />
        </div>
      </div>
    );
  }

  if (error || !process) {
    return (
      <div className="h-screen flex flex-col bg-surface-subtle">
        <div className="border-b border-line bg-white px-6 py-4">
          <Button variant="ghost" size="small" onClick={() => window.history.back()}>
            <ArrowLeft className="h-4 w-4" />
            {t('common.Back')}
          </Button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-destructive">{t(processErrorMessageKey(error))}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-surface-subtle">
      {/* Header */}
      <div className="border-b border-line bg-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="small" onClick={() => window.history.back()}>
            <ArrowLeft className="h-4 w-4" />
            {t('common.Back')}
          </Button>
          <div>
            <h1 className="text-lg font-semibold text-foreground">{process.name}</h1>
            <p className="text-sm text-muted-foreground">{t('processFlow.fullscreenTitle')}</p>
          </div>
        </div>
        {canViewSensitiveFields && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              {t('processFlow.viewMode.label')}
            </span>
            <Select
              value={viewMode}
              onValueChange={(value) => onViewModeChange(value as HeatmapViewMode)}
            >
              <SelectTrigger className="w-56" aria-label={t('processFlow.viewMode.label')}>
                <SelectValue placeholder={t('processFlow.viewMode.label')} />
              </SelectTrigger>
              <SelectContent>
                {HEATMAP_VIEW_MODES.map((mode) => (
                  <SelectItem key={mode} value={mode}>
                    {t(`processFlow.viewMode.${mode}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Fullscreen Flow */}
      <div className="relative flex-1 p-6">
        {canViewSensitiveFields && viewMode !== 'standard' && (
          <ProcessFlowHeatmapLegend mode={viewMode} maxHoursPerYear={maxHoursPerYear} />
        )}
        {layout.isPending ? (
          <Skeleton role="status" aria-label={t('common.loading')} className="h-full w-full" />
        ) : (
          <ProcessFlowVisualization
            process={process}
            overlays={overlays}
            height="100%"
            showMinimap
            showControls
            showSensitiveFields={canViewSensitiveFields}
            showVariantCheck={data?.capabilities?.canViewProcessInsights}
            savedPositions={layout.savedPositions}
            swimlaneOrder={layout.swimlaneOrder}
            laneColors={layout.laneColors}
            openAssignments={data?.openAssignments}
            events={data?.events}
            notes={data?.notes}
            assigneeLookup={assigneeLookup}
            entitySuggestions={entitySuggestions}
          />
        )}
      </div>
    </div>
  );
}
